import React, { useState, useEffect } from 'react';
import assetService from '../services/assetService';
import ScheduleMaintenance from './ScheduleMaintenance';
import { FaBoxes, FaCalendarAlt, FaWrench, FaClock, FaExclamationTriangle, FaCheckCircle, FaSearch } from 'react-icons/fa';
import '../styles/StockControl.css';

const FREQ_LABELS = {
  weekly: 'Weekly',
  biweekly: 'Bi-Weekly',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semi_annual: 'Semi-Annual',
  yearly: 'Yearly',
  custom: 'Custom'
};

const StockControl = () => {
  const [consumables, setConsumables] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [activeTab, setActiveTab] = useState('consumables');
  const [scheduleAsset, setScheduleAsset] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [assetsRes, schedulesRes] = await Promise.all([
        assetService.getAllAssets({}),
        assetService.getMaintenanceSchedules()
      ]);
      const consumableAssets = assetsRes.data.data.filter(asset => asset.assetType === 'consumable');
      setConsumables(consumableAssets);
      setSchedules(schedulesRes.data.data || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleQuantityChange = async (assetId, changeAmount) => {
    setUpdatingId(assetId);
    try {
      const response = await assetService.updateAssetQuantity(assetId, changeAmount);
      setConsumables(consumables.map(asset =>
        asset._id === assetId
          ? { ...asset, quantity: response.data.data.quantity, isLowStock: response.data.isLowStock }
          : asset
      ));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update quantity');
      setTimeout(() => setError(''), 3000);
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredConsumables = consumables.filter(asset =>
    asset.assetName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    asset.serialNumber?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredSchedules = schedules.filter(s =>
    s.assetName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.serialNumber?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    s.labLocation?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const lowStockConsumables = filteredConsumables.filter(asset => asset.isLowStock);

  const getScheduleBadge = (status, isScheduled) => {
    if (isScheduled) {
      if (status === 'in_progress') return <span className="sc-badge sc-badge-info"><FaWrench /> In Progress</span>;
      return <span className="sc-badge sc-badge-info"><FaCalendarAlt /> Scheduled</span>;
    }
    switch (status) {
      case 'overdue': return <span className="sc-badge sc-badge-danger"><FaExclamationTriangle /> Overdue</span>;
      case 'due_soon': return <span className="sc-badge sc-badge-warning"><FaClock /> Due Soon</span>;
      case 'on_track': return <span className="sc-badge sc-badge-success"><FaCheckCircle /> On Track</span>;
      case 'scheduled': return <span className="sc-badge sc-badge-info"><FaCalendarAlt /> Scheduled</span>;
      case 'in_progress': return <span className="sc-badge sc-badge-info"><FaWrench /> In Progress</span>;
      default: return <span className="sc-badge sc-badge-muted">No Schedule</span>;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getDaysUntil = (dateStr) => {
    if (!dateStr) return null;
    const diff = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
    return diff;
  };

  if (loading) {
    return <div className="loading">Loading data...</div>;
  }

  return (
    <div className="stock-control-container">
      {/* Tab Switcher */}
      <div className="sc-tab-bar">
        <button className={`sc-tab ${activeTab === 'consumables' ? 'active' : ''}`} onClick={() => setActiveTab('consumables')}>
          <FaBoxes /> Consumables Stock
        </button>
        <button className={`sc-tab ${activeTab === 'maintenance' ? 'active' : ''}`} onClick={() => setActiveTab('maintenance')}>
          <FaWrench /> Maintenance Schedules
        </button>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {/* Search */}
      <div className="search-box">
        <FaSearch className="search-icon" />
        <input
          type="text"
          placeholder={activeTab === 'consumables' ? 'Search consumables...' : 'Search assets by name, serial, lab...'}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Consumables Tab */}
      {activeTab === 'consumables' && (
        <>
          {lowStockConsumables.length > 0 && (
            <div className="alert alert-warning">
              <strong>⚠️ Warning:</strong> {lowStockConsumables.length} item(s) are running low on stock
            </div>
          )}

          {filteredConsumables.length === 0 ? (
            <div className="no-assets">No consumables found</div>
          ) : (
            <div className="stock-table-responsive">
              <table className="stock-table">
                <thead>
                  <tr>
                    <th>Asset Name</th>
                    <th>Category</th>
                    <th>Lab Location</th>
                    <th>Current Stock</th>
                    <th>Min Stock Level</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredConsumables.map((consumable) => (
                    <tr key={consumable._id} className={consumable.isLowStock ? 'low-stock-row' : ''}>
                      <td>
                        <strong>{consumable.assetName}</strong>
                        {consumable.isLowStock && <span className="stock-alert">LOW STOCK</span>}
                      </td>
                      <td>{consumable.category}</td>
                      <td>{consumable.labLocation}</td>
                      <td className="quantity-cell">
                        <span className={`quantity ${consumable.isLowStock ? 'critical' : ''}`}>{consumable.quantity}</span>
                      </td>
                      <td>{consumable.minQuantity}</td>
                      <td>
                        {consumable.isLowStock ? (
                          <span className="badge badge-danger">Critical Stock</span>
                        ) : (
                          <span className="badge badge-success">In Stock</span>
                        )}
                      </td>
                      <td className="action-cell">
                        <div className="quantity-controls">
                          <button onClick={() => handleQuantityChange(consumable._id, -1)} className="btn btn-sm btn-minus" disabled={updatingId === consumable._id || consumable.quantity === 0} title="Decrease quantity">−</button>
                          <span className="quantity-display">{consumable.quantity}</span>
                          <button onClick={() => handleQuantityChange(consumable._id, 1)} className="btn btn-sm btn-plus" disabled={updatingId === consumable._id} title="Increase quantity">+</button>
                        </div>
                        {updatingId === consumable._id && <span className="updating">Updating...</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="stock-stats">
            <div className="stat-item">
              <h4>Total Consumables</h4>
              <p>{filteredConsumables.length}</p>
            </div>
            <div className="stat-item">
              <h4>Low Stock Items</h4>
              <p className="stat-critical">{lowStockConsumables.length}</p>
            </div>
            <div className="stat-item">
              <h4>Total Units</h4>
              <p>{filteredConsumables.reduce((sum, c) => sum + c.quantity, 0)}</p>
            </div>
          </div>
        </>
      )}

      {/* Maintenance Schedules Tab */}
      {activeTab === 'maintenance' && (
        <>
          {/* Schedule Summary Cards */}
          <div className="sc-summary-row">
            <div className="sc-summary-card">
              <span className="sc-summary-val">{schedules.filter(s => s.scheduleStatus !== 'no_schedule').length}</span>
              <span className="sc-summary-label">With Cycles</span>
            </div>
            <div className="sc-summary-card" style={{ borderLeftColor: '#3b82f6' }}>
              <span className="sc-summary-val">{schedules.filter(s => s.isScheduled).length}</span>
              <span className="sc-summary-label">Scheduled</span>
            </div>
            <div className="sc-summary-card overdue">
              <span className="sc-summary-val">{schedules.filter(s => s.scheduleStatus === 'overdue').length}</span>
              <span className="sc-summary-label">Overdue</span>
            </div>
            <div className="sc-summary-card due-soon">
              <span className="sc-summary-val">{schedules.filter(s => s.scheduleStatus === 'due_soon').length}</span>
              <span className="sc-summary-label">Due Soon</span>
            </div>
            <div className="sc-summary-card on-track">
              <span className="sc-summary-val">{schedules.filter(s => s.scheduleStatus === 'on_track').length}</span>
              <span className="sc-summary-label">On Track</span>
            </div>
          </div>

          {filteredSchedules.length === 0 ? (
            <div className="no-assets">No non-consumable assets found</div>
          ) : (
            <div className="stock-table-responsive">
              <table className="stock-table sc-schedule-table">
                <thead>
                  <tr>
                    <th>Asset Name</th>
                    <th>Lab</th>
                    <th>Frequency</th>
                    <th>Last Maintenance</th>
                    <th>Next Due</th>
                    <th>Assigned To</th>
                    <th>Days Until Due</th>
                    <th>Schedule Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSchedules.map((sch) => {
                    const daysUntil = getDaysUntil(sch.nextDue);
                    return (
                      <tr key={sch._id} className={sch.isScheduled ? 'scheduled-row' : sch.scheduleStatus === 'overdue' ? 'overdue-row' : sch.scheduleStatus === 'due_soon' ? 'due-soon-row' : ''}>
                        <td>
                          <strong>{sch.assetName}</strong>
                          {sch.serialNumber && <div className="sc-serial">{sch.serialNumber}</div>}
                        </td>
                        <td>{sch.labLocation}</td>
                        <td>
                          {sch.frequency && sch.frequency !== 'no_schedule'
                            ? <span className="sc-freq-badge">{FREQ_LABELS[sch.frequency] || `${sch.cycleDays} days`}</span>
                            : <span className="sc-freq-badge none">Not Set</span>}
                        </td>
                        <td>{formatDate(sch.lastMaintenance)}</td>
                        <td>{formatDate(sch.nextDue)}</td>
                        <td>
                          {sch.isScheduled && sch.scheduledTo ? (
                            <span className="sc-tech-badge">{sch.scheduledTo}</span>
                          ) : (
                            <span style={{ color: '#94a3b8' }}>—</span>
                          )}
                        </td>
                        <td>
                          {daysUntil !== null ? (
                            <span className={`sc-days ${daysUntil < 0 ? 'overdue' : daysUntil <= 7 ? 'soon' : 'ok'}`}>
                              {daysUntil < 0 ? `${Math.abs(daysUntil)}d overdue` : `${daysUntil}d`}
                            </span>
                          ) : '—'}
                        </td>
                        <td>{getScheduleBadge(sch.scheduleStatus, sch.isScheduled)}</td>
                        <td>
                          <button
                            className="sc-schedule-btn"
                            onClick={() => setScheduleAsset(sch)}
                            title={sch.isScheduled ? "Reschedule maintenance" : "Schedule maintenance"}
                            disabled={sch.isScheduled}
                          >
                            <FaCalendarAlt /> {sch.isScheduled ? 'Scheduled' : 'Schedule'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      {/* Schedule Maintenance Modal */}
      {scheduleAsset && (
        <ScheduleMaintenance
          asset={scheduleAsset}
          onClose={() => setScheduleAsset(null)}
          onScheduled={() => fetchData()}
        />
      )}
    </div>
  );
};

export default StockControl;
